import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  Wallet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Settings,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

/* ------------------------------------------------------------------ */
/*  Data — sourced from actual project files                          */
/* ------------------------------------------------------------------ */

const LEAVE_TYPES = [
  { code: "CL", name: "Casual Leave", paid: true, limit: "12 days/year", desc: "Half-day allowed. Max 5 consecutive days." },
  { code: "SL", name: "Sick Leave", paid: true, limit: "12 days/year", desc: "Half-day allowed. Document required after 2 consecutive days." },
  { code: "EL", name: "Earned Leave", paid: true, limit: "15 days/year", desc: "Monthly accrual (1.25 days/month). Carry-forward up to 30 days." },
  { code: "PL", name: "Privilege Leave", paid: true, limit: "15 days/year", desc: "Monthly accrual (1.25 days/month). Carry-forward up to 30 days." },
  { code: "LOP", name: "Loss Of Pay", paid: false, limit: "No limit", desc: "Unpaid leave. Reduces payable days in payroll." },
  { code: "ML", name: "Maternity Leave", paid: true, limit: "182 days/year", desc: "Female employees only." },
  { code: "PTL", name: "Paternity Leave", paid: true, limit: "15 days/year", desc: "Male employees only." },
  { code: "BL", name: "Bereavement Leave", paid: true, limit: "5 days/year", desc: "Half-day allowed." },
  { code: "MRL", name: "Marriage Leave", paid: true, limit: "5 days/year", desc: "No half-day." },
  { code: "CO", name: "Comp Off", paid: true, limit: "No limit", desc: "Compensatory off for extra work days." },
  { code: "OH", name: "Optional Holiday", paid: true, limit: "2 days/year", desc: "Employee-chosen holidays from a predefined list." },
  { code: "EML", name: "Emergency Leave", paid: true, limit: "3 days/year", desc: "Half-day allowed." },
];

const LEAVE_STATUSES = [
  { code: "Pending", desc: "Request submitted, awaiting manager or HR approval.", tint: "ic-amber" },
  { code: "Approved", desc: "Leave approved. Attendance and balance are updated.", tint: "ic-green" },
  { code: "Rejected", desc: "Leave request was declined by an approver.", tint: "ic-rose" },
  { code: "Escalated", desc: "Manager approved; awaiting HR for final approval.", tint: "ic-blue" },
  { code: "Cancelled", desc: "Leave request was cancelled by the employee or admin.", tint: "ic-slate" },
];

const APPROVAL_STEPS = [
  { num: 1, label: "Employee Applies", desc: "Employee submits a leave request with type, dates, and reason." },
  { num: 2, label: "Manager Review", desc: "Reporting manager reviews and approves or rejects the request." },
  { num: 3, label: "HR Review (if required)", desc: "If HR approval is configured, the request is escalated to HR after manager approval." },
  { num: 4, label: "Approved / Rejected", desc: "Final decision is recorded. Leave balance and attendance are updated automatically." },
];

const DASHBOARD_TABS = [
  { label: "Dashboard", desc: "Overview of leave activity, stats, balances, upcoming leave, and team availability.", roles: "All roles" },
  { label: "Apply", desc: "Submit leave requests. Select leave type, dates, half-day option, and reason.", roles: "HR, Manager, Employee" },
  { label: "Balances", desc: "View leave balances per employee per type. Allocate leave manually or auto-allocate from policies.", roles: "All roles" },
  { label: "Approve", desc: "Review and act on pending leave requests from your team.", roles: "Admin, HR, Manager" },
  { label: "Analytics", desc: "Leave usage trends, department-wise utilisation, and LOP insights.", roles: "Admin, HR" },
  { label: "Admin", desc: "5-step setup wizard: org settings, leave package, leave types, holiday calendar, and balance allocation.", roles: "Admin only" },
];

const FAQ_DATA = [
  {
    q: "What is the Leave Management module?",
    a: "The Leave module in BrixiGo lets you configure leave types and policies, assign leave balances to employees, handle leave requests with multi-level approval, and track leave utilisation across the organisation.",
  },
  {
    q: "How do I set up leave for the first time?",
    a: "Go to Leave > Admin tab. The 5-step wizard guides you through: organisation leave settings, choosing a leave package (Basic with 6 types, Advanced with 11 types, or Custom), reviewing leave types, setting up the holiday calendar, and allocating leave balances to employees.",
  },
  {
    q: "What are the available leave packages?",
    a: "BrixiGo offers three packages: Basic (CL, SL, EL, LOP, ML, PTL — 6 types), Advanced (adds BL, MRL, CO, OH, EML — 11 types), or Custom (pick from all 18 types). Applying a package auto-seeds leave types, policies, the Indian holiday catalog, and allocates balances to active employees.",
  },
  {
    q: "How do I apply for leave?",
    a: "Go to Leave > Apply tab. Select the employee, leave type, from/to dates, and duration (Full Day, First Half, or Second Half). The form shows your current balance for the selected type. Add an optional reason and submit.",
  },
  {
    q: "How does the approval workflow work?",
    a: "Leave requests go to the reporting manager first (shown as 'Awaiting manager'). If HR approval is configured, approved requests are then escalated to HR (shown as 'Awaiting HR'). Admins can also finalise approval directly. Both approve and reject actions are available at each level.",
  },
  {
    q: "How do leave balances work?",
    a: "Each employee has a balance per leave type showing: opening balance, accrued days (from monthly accruals), consumed days, available days, carried-forward days, and LOP days. Balances can be allocated manually (specific days per type) or auto-allocated based on policies, prorated from the employee's date of joining.",
  },
  {
    q: "What happens when leave balance is zero?",
    a: "If no balance remains for the selected leave type, BrixiGo warns that the request will incur Loss of Pay (LOP). LOP days reduce payable days in payroll, directly affecting salary calculations.",
  },
  {
    q: "How does leave affect attendance?",
    a: "When a leave request is approved, the attendance record is automatically updated. Leave days are marked with status 'L' (Leave) in attendance. Paid leave (PL) and unpaid leave (UL) are both tracked in the attendance system.",
  },
  {
    q: "How does leave affect payroll?",
    a: "Payroll uses attendance data (including leave status) to compute payable days. LOP days reduce payable days and proportionally reduce earnings. When LOP is enabled in payroll settings, unpaid absence days automatically reduce prorated earnings.",
  },
  {
    q: "Where can I configure leave types and policies?",
    a: "Go to Settings > Leave Settings. The page has three tabs: Leave Types (add/edit types with code, name, paid status, and annual limit), Leave Policies (entitlement, carry-forward, accrual method), and Leave Approval Workflow (multi-level approval chain configuration).",
  },
  {
    q: "Can I run monthly leave accrual?",
    a: "Yes. For leave types configured with monthly accrual (like EL and PL at 1.25 days/month), you can trigger monthly accrual from the Balances tab. This adds the accrued days to each employee's balance.",
  },
  {
    q: "What leave reports are available?",
    a: "Go to Reports > Leave. BrixiGo offers four reports: Leave Balance Report (current balances), Leave Register (all transactions for a period), Leave Utilisation Report (availed vs remaining by department), and Pending Leave Requests. All reports can be exported to Excel, CSV, or PDF.",
  },
];

const ERROR_SOLUTIONS = [
  {
    error: "Leave Code and Leave Name are required",
    cause: "When creating a leave type in Settings, the code or name field was left blank.",
    fix: "Enter both the Leave Code (e.g. CL) and Leave Name (e.g. Casual Leave) before saving.",
  },
  {
    error: "No balance remaining — this request will incur Loss of Pay (LOP)",
    cause: "The employee's balance for the selected leave type is zero.",
    fix: "This is a warning, not an error. The request can still be submitted, but the days will be counted as LOP and reduce payable days in payroll. To avoid this, allocate additional balance from the Balances tab.",
  },
  {
    error: "Organisation context missing. Leave needs a valid organisation session.",
    cause: "The leave module cannot determine the current organisation.",
    fix: "Ensure you are logged in and your account is linked to an organisation. Refresh the page. If the issue persists, log out and log back in.",
  },
  {
    error: "No active employees found to allocate leave balances",
    cause: "The system found no active employees when trying to allocate or auto-allocate balances.",
    fix: "Add employees first via the Employee module, then return to Leave > Admin or Balances to allocate.",
  },
  {
    error: "Could not apply leave package",
    cause: "The leave package setup failed during the Admin wizard.",
    fix: "Check your internet connection and try again. Ensure the organisation setup is complete (departments, designations, locations).",
  },
  {
    error: "Failed to apply leave",
    cause: "The leave request submission failed due to a server or validation error.",
    fix: "Verify all required fields are filled (employee, leave type, dates). Check that the date range is valid (from date is not in the past, to date is on or after from date). Try again.",
  },
  {
    error: "Allocation failed / Auto-allocation failed",
    cause: "Manual or automatic leave balance allocation encountered an error.",
    fix: "For manual allocation, ensure you have selected a leave type, entered valid days, and selected at least one employee. For auto-allocation, ensure leave policies are configured first.",
  },
  {
    error: "Policy Name and Leave Type are required",
    cause: "When creating a leave policy in Settings, a required field was left blank.",
    fix: "Enter the Policy Name and select a Leave Type from the dropdown before saving.",
  },
  {
    error: "Workflow Name and Level 1 Approver are required",
    cause: "When creating an approval workflow, the name or first-level approver was not set.",
    fix: "Enter a workflow name and select a Level 1 Approver (e.g. Reporting Manager, Department Head) before saving.",
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function LeaveHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);

  const lc = (s) => s.toLowerCase();
  const match = (s) => lc(s).includes(lc(search));

  const filteredFaqs = search.trim()
    ? FAQ_DATA.filter((f) => match(f.q) || match(f.a))
    : FAQ_DATA;

  const filteredErrors = search.trim()
    ? ERROR_SOLUTIONS.filter((e) => match(e.error) || match(e.cause) || match(e.fix))
    : ERROR_SOLUTIONS;

  return (
    <div className="brix-page">
      {/* Hero */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-teal">
              <CalendarDays size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Leave Management</h1>
              <p className="hs-hero-sub">
                Understand how to configure, apply, approve, and manage leave in BrixiGo.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search — e.g. leave type, carry forward, approval..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      {/* ── 1. Overview ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Leave Management Overview</h2>
        </div>
        <div className="ah-content-card">
          <p>
            The Leave Management module in BrixiGo lets you configure leave types and policies,
            assign leave balances, handle leave requests with multi-level approval, and track
            leave utilisation across your organisation.
          </p>
          <p>
            The Leave module is accessed from the sidebar under <strong>Leave</strong> and provides
            six tabs based on your role:
          </p>
          <div className="ah-manage-grid">
            {DASHBOARD_TABS.map((tab) => (
              <div key={tab.label} className="ah-manage-card">
                <div className="ah-manage-title">{tab.label}</div>
                <div className="ah-status-desc">{tab.desc}</div>
                <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--brix-slate-400)", marginTop: 6 }}>{tab.roles}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2. Leave Configuration ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Settings size={20} className="ah-section-icon ic-slate" />
          <h2 className="hs-section-title">Leave Configuration</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Leave is configured in two places: the <strong>Leave Admin tab</strong> (5-step setup wizard
            for first-time setup) and <strong>Settings &gt; Leave Settings</strong> (for ongoing management).
          </p>

          <p style={{ fontWeight: 700 }}>Admin Tab — 5-Step Setup Wizard:</p>
          <div className="ah-workflow-steps">
            {[
              { num: 1, label: "Organisation Settings", desc: "Set leave year cycle (Calendar Jan–Dec or Financial Apr–Mar)." },
              { num: 2, label: "Choose Leave Package", desc: "Select Basic (6 types), Advanced (11 types), or Custom. Auto-seeds types, policies, and holidays." },
              { num: 3, label: "Review Leave Types", desc: "View and manage the active leave types. Apply to all or specific employees." },
              { num: 4, label: "Holiday Calendar", desc: "Review auto-seeded Indian holidays or manage custom holidays." },
              { num: 5, label: "Allocate Balances", desc: "Manual allocation (type + days + employees) or auto-allocate prorated by date of joining." },
            ].map((step, idx, arr) => (
              <div key={step.num} className="ah-workflow-step">
                <div className="ah-workflow-num">{step.num}</div>
                <div className="ah-workflow-body">
                  <div className="ah-workflow-label">{step.label}</div>
                  <div className="ah-workflow-desc">{step.desc}</div>
                </div>
                {idx < arr.length - 1 && <div className="ah-workflow-connector" />}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 20 }}>
            <p style={{ fontWeight: 700 }}>Settings &gt; Leave Settings — 3 Tabs:</p>
            <div className="ah-feature-grid">
              <div className="ah-feature-item">
                <ClipboardList size={16} className="ic-blue" />
                <span><strong>Leave Types</strong> — code, name, paid/unpaid, annual limit</span>
              </div>
              <div className="ah-feature-item">
                <Settings size={16} className="ic-amber" />
                <span><strong>Leave Policies</strong> — entitlement, carry-forward, accrual method</span>
              </div>
              <div className="ah-feature-item">
                <CheckCircle2 size={16} className="ic-green" />
                <span><strong>Approval Workflow</strong> — multi-level approver chain</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. Leave Types ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CalendarDays size={20} className="ah-section-icon ic-teal" />
          <h2 className="hs-section-title">Leave Types</h2>
        </div>
        <div className="ah-content-card">
          <p>
            BrixiGo supports up to 18 leave types. The types available to your organisation depend on
            which package (Basic, Advanced, or Custom) was applied. Here are the most common types:
          </p>
          <div className="ah-status-grid">
            {LEAVE_TYPES.map((lt) => (
              <div key={lt.code} className="ah-status-item">
                <div className="ah-status-header">
                  <span className={`ah-status-code ${lt.paid ? "ic-green" : "ic-rose"}`}>{lt.code}</span>
                  <span className="ah-status-label">{lt.name}</span>
                </div>
                <div className="ah-status-desc">
                  {lt.desc} <span style={{ fontWeight: 600 }}>({lt.paid ? "Paid" : "Unpaid"} · {lt.limit})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. Leave Balances & Allocation ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Wallet size={20} className="ah-section-icon ic-amber" />
          <h2 className="hs-section-title">Leave Balances & Allocation</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Each employee has a leave balance per type. The Balances tab shows wallet-style cards
            with balance details:
          </p>
          <div className="ah-info-chips" style={{ marginBottom: 14 }}>
            {["Opening Balance", "Accrued", "Consumed", "Available", "Carried Forward", "LOP Days"].map((f) => (
              <span key={f} className="ah-info-chip">{f}</span>
            ))}
          </div>

          <p style={{ fontWeight: 700 }}>Allocation Methods:</p>
          <div className="ah-payroll-info">
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Auto-allocate from policies</strong> — prorates leave from each employee's date of joining</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Manual allocation</strong> — specify leave type, period year, number of days, and optionally specific employees</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Package-based allocation</strong> — when applying a leave package, balances are auto-allocated to all active employees</span>
            </div>
            <div className="ah-payroll-item ah-payroll-note">
              <Info size={16} />
              <span><strong>Monthly accrual</strong> can be triggered for types like EL and PL (1.25 days/month)</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. Leave Requests ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <ClipboardList size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Leave Requests</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Employees apply for leave from the <strong>Apply</strong> tab. The request form requires:
          </p>
          <div className="ah-info-chips" style={{ marginBottom: 14 }}>
            {["Employee", "Leave Type", "From Date", "To Date", "Duration (Full/Half Day)", "Reason (optional)"].map((f) => (
              <span key={f} className="ah-info-chip">{f}</span>
            ))}
          </div>
          <p>
            The form shows the current balance for the selected leave type. If the balance is zero,
            a warning appears: the request will incur LOP. Half-day leave (First Half / Second Half)
            is available when the from and to dates are the same. A confirmation dialog appears before
            submission.
          </p>
        </div>
      </section>

      {/* ── 6. Leave Approval Workflow ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CheckCircle2 size={20} className="ah-section-icon ic-green" />
          <h2 className="hs-section-title">Leave Approval</h2>
        </div>
        <div className="ah-content-card">
          <p>BrixiGo supports multi-level leave approval:</p>
          <div className="ah-workflow-steps">
            {APPROVAL_STEPS.map((step, idx) => (
              <div key={step.num} className="ah-workflow-step">
                <div className="ah-workflow-num">{step.num}</div>
                <div className="ah-workflow-body">
                  <div className="ah-workflow-label">{step.label}</div>
                  <div className="ah-workflow-desc">{step.desc}</div>
                </div>
                {idx < APPROVAL_STEPS.length - 1 && <div className="ah-workflow-connector" />}
              </div>
            ))}
          </div>

          <p style={{ marginTop: 16, fontWeight: 700 }}>Leave Statuses:</p>
          <div className="ah-status-grid">
            {LEAVE_STATUSES.map((s) => (
              <div key={s.code} className="ah-status-item">
                <div className="ah-status-header">
                  <span className={`ah-status-code ${s.tint}`}>{s.code}</span>
                </div>
                <div className="ah-status-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. Leave & Attendance ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <CalendarDays size={20} className="ah-section-icon ic-green" />
          <h2 className="hs-section-title">Leave & Attendance</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Leave and attendance are connected in BrixiGo:
          </p>
          <div className="ah-payroll-info">
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span>When a leave request is <strong>approved</strong>, the attendance record is automatically updated</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span>Leave days are marked as <strong>L</strong> (Leave) in the attendance calendar</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span>Both <strong>paid leave (PL)</strong> and <strong>unpaid leave (UL)</strong> are tracked in attendance</span>
            </div>
            <div className="ah-payroll-item ah-payroll-note">
              <Info size={16} />
              <span>The Holiday Calendar is shared between the Leave and Attendance modules</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. Leave & Payroll ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <Wallet size={20} className="ah-section-icon ic-rose" />
          <h2 className="hs-section-title">Leave & Payroll</h2>
        </div>
        <div className="ah-content-card">
          <p>
            Leave affects payroll through Loss of Pay (LOP):
          </p>
          <div className="ah-payroll-info">
            <div className="ah-payroll-item ah-payroll-no">
              <XCircle size={16} />
              <span><strong>LOP days</strong> reduce payable days in payroll, directly reducing salary earnings</span>
            </div>
            <div className="ah-payroll-item ah-payroll-note">
              <Info size={16} />
              <span>When LOP is enabled in payroll settings, unpaid absence days automatically reduce prorated earnings</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span><strong>Paid leave</strong> does not reduce payable days — salary is unaffected</span>
            </div>
            <div className="ah-payroll-item ah-payroll-yes">
              <CheckCircle2 size={16} />
              <span>The Leave Balances tab shows <strong>LOP days incurred</strong> per employee to help identify payroll impact</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. FAQs ── */}
      <section className="ah-section" ref={faqRef}>
        <div className="ah-section-header">
          <Info size={20} className="ah-section-icon ic-blue" />
          <h2 className="hs-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="ah-content-card">
          <div className="hs-faq-list">
            {filteredFaqs.length === 0 && (
              <p className="ah-empty">No matching questions found.</p>
            )}
            {filteredFaqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={faq.q} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{faq.q}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">{faq.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 10. Common Errors ── */}
      <section className="ah-section">
        <div className="ah-section-header">
          <AlertTriangle size={20} className="ah-section-icon ic-rose" />
          <h2 className="hs-section-title">Common Leave Issues & Solutions</h2>
        </div>
        <div className="ah-content-card">
          {filteredErrors.length === 0 && (
            <p className="ah-empty">No matching errors found.</p>
          )}
          <div className="ah-errors-list">
            {filteredErrors.map((item) => (
              <div key={item.error} className="ah-error-item">
                <div className="ah-error-title">
                  <AlertTriangle size={14} />
                  <code>{item.error}</code>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Why:</span>
                  <span>{item.cause}</span>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Fix:</span>
                  <span>{item.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
